import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { saveUserSettings } from '../features/userSettings/userSettingsSlice';

const USER_SETTING_SEEN_POPOVERS = 'seenPopovers';

/**
 * Hook to manage contextual popovers that show once and persist dismissed state.
 * 
 * @param {string} popoverKey - Unique identifier for this popover
 * @returns {{ show: boolean, dismiss: () => void, anchorEl: any, setAnchorEl: (el: any) => void }}
 */
const useContextualPopover = (popoverKey) => {
  const dispatch = useDispatch();
  const userSettings = useSelector((state) => state.userSettings.settings);
  const settingsLoaded = useSelector((state) => state.userSettings.loaded);
  const [anchorEl, setAnchorEl] = useState(null);
  // Local state to track dismissal immediately (before async API call completes)
  const [dismissed, setDismissed] = useState(false);

  // Memoize seenPopovers to avoid unstable reference (LOW priority fix)
  const seenPopovers = useMemo(
    () => userSettings[USER_SETTING_SEEN_POPOVERS] || [],
    [userSettings[USER_SETTING_SEEN_POPOVERS]]
  );
  const hasBeenSeen = Array.isArray(seenPopovers) && seenPopovers.includes(popoverKey);

  // Show popover only if:
  // 1. Settings have finished loading (prevents flash for returning users)
  // 2. Popover hasn't been dismissed locally
  // 3. Popover hasn't been seen in Redux (persisted state)
  // 4. Anchor element is set
  const show = settingsLoaded && !dismissed && !hasBeenSeen && Boolean(anchorEl);

  // Dismiss function sets local state immediately, then persists to API
  const dismiss = useCallback(() => {
    // Set local dismissed state immediately to prevent reappearance during API call
    setDismissed(true);
    setAnchorEl(null);
    
    // Add this popover key to the seen list
    const updatedSeenPopovers = Array.isArray(seenPopovers) 
      ? [...seenPopovers, popoverKey]
      : [popoverKey];

    // Spread existing settings to avoid overwriting other user settings (API uses PUT)
    dispatch(
      saveUserSettings({
        ...userSettings,
        [USER_SETTING_SEEN_POPOVERS]: updatedSeenPopovers,
      })
    );
  }, [dispatch, popoverKey, seenPopovers, userSettings]);

  // Reset local dismissed state when settings reload (e.g., page refresh)
  useEffect(() => {
    if (settingsLoaded) {
      setDismissed(false);
    }
  }, [settingsLoaded]);

  return {
    show,
    dismiss,
    anchorEl,
    setAnchorEl,
  };
};

export default useContextualPopover;
